<template>
  <div class="min-h-screen bg-gray-50">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="mb-6">
        <h1 class="text-3xl font-bold text-gray-900">Dashboard Financeiro</h1>
        <p class="text-gray-600 mt-1">Análise das receitas e despesas</p>
      </div>

      <div class="bg-white rounded-lg shadow-sm p-6 mb-6">
        <h2 class="text-lg font-semibold text-gray-900 mb-4">Filtros</h2>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <PropertySelectAsync
              v-model="filters.propertyId"
              label="Imóvel"
              placeholder="Todos os imóveis"
              :required="false"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Proprietário</label>
            <PersonSelectAsync
              v-model="filters.ownerId"
              role="owner"
              placeholder="Todos os proprietários"
              :allow-create="false"
              :show-whats-app-link="false"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Responsável</label>
            <SelectWithFilter
              v-model="filters.responsibleId"
              :options="responsibleUserOptions"
              placeholder="Todos os responsáveis"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Corretor</label>
            <PersonSelectAsync
              v-model="filters.corretorId"
              role="agent"
              placeholder="Todos os corretores"
              :allow-create="false"
              :show-whats-app-link="false"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Data Início *</label>
            <input
              v-model="filters.startDate"
              type="date"
              required
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Data Fim</label>
            <input
              v-model="filters.endDate"
              type="date"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div class="flex items-end justify-end md:col-span-3">
            <button
              type="button"
              @click="() => loadBookings(1)"
              class="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition space-x-2"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <span>Buscar</span>
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="dateError"
        class="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
      >
        {{ dateError }}
      </div>

      <div v-if="loading" class="flex justify-center items-center h-64">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>

      <div v-else>
        <!-- Cards de Resumo -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <!-- A Receber -->
          <div class="bg-white rounded-lg shadow-sm p-6 border-l-4 border-green-600">
            <div class="flex items-center justify-between mb-4">
              <div>
                <p class="text-sm font-medium text-gray-600 uppercase">Total a Receber</p>
                <p class="text-xs text-gray-500 mt-1">De hóspedes</p>
              </div>
              <div class="bg-green-100 rounded-full p-3">
                <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                </svg>
              </div>
            </div>
            <p class="text-3xl font-bold text-gray-900">{{ formatCurrency(totals.totalDue) }}</p>
            <div class="mt-3 pt-3 border-t border-gray-100">
              <p class="text-sm text-gray-600">
                Recebido:
                <span class="font-semibold text-green-600">{{ formatCurrency(totals.totalReceived) }}</span>
              </p>
              <p class="text-sm text-gray-600 mt-1">
                Saldo:
                <span class="font-semibold text-orange-600">{{ formatCurrency(totals.balanceDue) }}</span>
              </p>
            </div>
          </div>

          <!-- A Pagar -->
          <div class="bg-white rounded-lg shadow-sm p-6 border-l-4 border-red-600">
            <div class="flex items-center justify-between mb-4">
              <div>
                <p class="text-sm font-medium text-gray-600 uppercase">Total a Pagar</p>
                <p class="text-xs text-gray-500 mt-1">Comissões, Serviços e Despesas Extras</p>
              </div>
              <div class="bg-red-100 rounded-full p-3">
                <svg class="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4" />
                </svg>
              </div>
            </div>
            <p class="text-3xl font-bold text-gray-900">{{ formatCurrency(totals.totalPayable) }}</p>
            <div class="mt-3 pt-3 border-t border-gray-100">
              <p class="text-sm text-gray-600">
                Pago:
                <span class="font-semibold text-green-600">{{ formatCurrency(totals.totalPaid) }}</span>
              </p>
              <p class="text-sm text-gray-600 mt-1">
                Saldo:
                <span class="font-semibold text-orange-600">{{ formatCurrency(totals.balancePayable) }}</span>
              </p>
            </div>
          </div>

          <!-- Balanço -->
          <div class="bg-white rounded-lg shadow-sm p-6 border-l-4" :class="balance >= 0 ? 'border-indigo-600' : 'border-yellow-600'">
            <div class="flex items-center justify-between mb-4">
              <div>
                <p class="text-sm font-medium text-gray-600 uppercase">Balanço</p>
                <p class="text-xs text-gray-500 mt-1">Receitas - Despesas</p>
              </div>
              <div class="rounded-full p-3" :class="balance >= 0 ? 'bg-indigo-100' : 'bg-yellow-100'">
                <svg class="w-6 h-6" :class="balance >= 0 ? 'text-indigo-600' : 'text-yellow-600'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
            <p class="text-3xl font-bold" :class="balance >= 0 ? 'text-indigo-600' : 'text-yellow-600'">
              {{ formatCurrency(balance) }}
            </p>
            <p class="text-sm mt-3 pt-3 border-t border-gray-100" :class="balance >= 0 ? 'text-green-600' : 'text-yellow-600'">
              {{ balance >= 0 ? '✓ Positivo' : '⚠ Atenção ao fluxo' }}
            </p>
          </div>
        </div>

        <!-- Tabela de Hospedagens -->
        <div class="bg-white rounded-lg shadow-sm overflow-hidden mb-8">
          <div class="px-4 sm:px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h2 class="text-lg font-semibold text-gray-900">Detalhamento das movimentações</h2>
            <div class="flex items-center gap-1.5 overflow-x-auto pb-1">
              <button
                type="button"
                @click="setFilterType('all')"
                :class="filterType === 'all' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'"
                class="px-2.5 sm:px-3 py-1 rounded-md text-sm font-medium transition whitespace-nowrap flex-shrink-0"
              >
                Todas
              </button>
              <button
                type="button"
                @click="setFilterType('booking')"
                :class="filterType === 'booking' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'"
                class="px-2.5 sm:px-3 py-1 rounded-md text-sm font-medium transition whitespace-nowrap flex-shrink-0"
              >
                Hospedagens
              </button>
              <button
                type="button"
                @click="setFilterType('cleaning')"
                :class="filterType === 'cleaning' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'"
                class="px-2.5 sm:px-3 py-1 rounded-md text-sm font-medium transition whitespace-nowrap flex-shrink-0"
              >
                Serviços
              </button>
              <button
                type="button"
                @click="setFilterType('expense')"
                :class="filterType === 'expense' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'"
                class="px-2.5 sm:px-3 py-1 rounded-md text-sm font-medium transition whitespace-nowrap flex-shrink-0"
              >
                Despesas
              </button>
            </div>
          </div>

          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hóspede/Prestador</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Imóvel</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Check-in</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Check-out</th>
                  <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Receitas</th>
                  <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Despesas</th>
                  <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Margem</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="booking in bookings" :key="booking.id" class="hover:bg-gray-50">
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="flex flex-col">
                      <span
                        v-if="booking.type === 'Service'"
                        class="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-800 font-medium"
                      >
                        Serviço - {{ truncate(booking.service_type_name, 10) }}
                      </span>
                      <span
                        v-else-if="booking.type === 'Expense'"
                        class="text-xs px-2 py-1 rounded-full bg-red-100 text-red-800 font-medium"
                      >
                        Despesa
                      </span>
                      <span
                        v-else
                        class="text-xs px-2 py-1 rounded-full bg-indigo-100 text-indigo-800 font-medium"
                      >
                        Hospedagem
                      </span>
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {{ truncate(booking.guest_name, 20) }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {{ truncate(booking.property_name, 15) }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {{ formatDate(booking.check_in_date) }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {{ formatDate(booking.check_out_date) }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-green-600">
                    {{ formatCurrency(booking.total_due || 0) }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-red-600">
                    {{ formatCurrency(booking.total_payable || 0) }}
                  </td>
                  <td
                    class="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold"
                    :class="getMarginColor(booking)"
                  >
                    {{ formatCurrency(calculateMargin(booking)) }}
                  </td>
                </tr>
                <tr v-if="bookings.length === 0">
                  <td colspan="8" class="px-6 py-12 text-center text-gray-500">
                    Nenhuma hospedagem encontrada
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            v-if="serverPagy && serverPagy.pages > 1"
            class="px-6 py-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-600"
          >
            <div>
              Página {{ serverPagy.page }} de {{ serverPagy.pages }}
              <span v-if="serverPagy.count != null" class="text-gray-500">
                ({{ serverPagy.from }}–{{ serverPagy.to }} de {{ serverPagy.count }})
              </span>
            </div>
            <div class="flex items-center space-x-2">
              <button
                type="button"
                @click="prevPage"
                :disabled="!serverPagy.prev"
                class="px-2 py-1 rounded border text-xs"
                :class="!serverPagy.prev ? 'border-gray-200 text-gray-300 cursor-not-allowed' : 'border-gray-300 text-gray-700 hover:bg-gray-50'"
              >
                Anterior
              </button>
              <button
                type="button"
                @click="nextPage"
                :disabled="!serverPagy.next"
                class="px-2 py-1 rounded border text-xs"
                :class="!serverPagy.next ? 'border-gray-200 text-gray-300 cursor-not-allowed' : 'border-gray-300 text-gray-700 hover:bg-gray-50'"
              >
                Próxima
              </button>
            </div>
          </div>
        </div>

        <!-- Resumo por Período -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div class="bg-white rounded-lg shadow-sm p-6">
            <h3 class="text-lg font-semibold text-gray-900 mb-4">Distribuição de Valores</h3>
            <div class="space-y-4">
              <div>
                <div class="flex justify-between items-center mb-2">
                  <span class="text-sm font-medium text-gray-600">Receitas (A Receber)</span>
                  <span class="text-sm font-semibold text-indigo-600">{{ formatCurrency(totals.totalDue) }}</span>
                </div>
                <div class="w-full bg-gray-200 rounded-full h-3">
                  <div class="bg-emerald-600 h-3 rounded-full" :style="{ width: getPercentage(totals.totalDue) + '%' }"></div>
                </div>
              </div>
              <div>
                <div class="flex justify-between items-center mb-2">
                  <span class="text-sm font-medium text-gray-600">Serviços (A Pagar)</span>
                  <span class="text-sm font-semibold text-amber-600">{{ formatCurrency(totals.totalServicesPayable) }}</span>
                </div>
                <div class="w-full bg-gray-200 rounded-full h-3">
                  <div class="bg-amber-500 h-3 rounded-full" :style="{ width: getPercentage(totals.totalServicesPayable) + '%' }"></div>
                </div>
              </div>
              <div>
                <div class="flex justify-between items-center mb-2">
                  <span class="text-sm font-medium text-gray-600">Despesas Extras (A Pagar)</span>
                  <span class="text-sm font-semibold text-red-600">{{ formatCurrency(totals.totalExpensesPayable) }}</span>
                </div>
                <div class="w-full bg-gray-200 rounded-full h-3">
                  <div class="bg-red-600 h-3 rounded-full" :style="{ width: getPercentage(totals.totalExpensesPayable) + '%' }"></div>
                </div>
              </div>

              <div class="pt-3 border-t border-gray-100">
                <div class="flex justify-between items-center">
                  <span class="text-sm font-medium text-gray-700">Resultado (Receitas - Despesas)</span>
                  <span
                    class="text-sm font-semibold"
                    :class="balance >= 0 ? 'text-green-600' : 'text-red-600'"
                  >
                    {{ formatCurrency(balance) }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="bg-white rounded-lg shadow-sm p-6">
            <h3 class="text-lg font-semibold text-gray-900 mb-4">Estatísticas</h3>
            <div class="space-y-3">
              <div class="flex justify-between items-center py-2 border-b border-gray-100">
                <span class="text-sm text-gray-600">Total de Hospedagens</span>
                <span class="text-sm font-semibold text-gray-900">{{ bookingCount }}</span>
              </div>
              <div class="flex justify-between items-center py-2 border-b border-gray-100">
                <span class="text-sm text-gray-600">Total de Serviços</span>
                <span class="text-sm font-semibold text-gray-900">{{ servicesCount }}</span>
              </div>
              <div class="flex justify-between items-center py-2 border-b border-gray-100">
                <span class="text-sm text-gray-600">Total de Despesas Extras</span>
                <span class="text-sm font-semibold text-gray-900">
                  {{ statsExpensesCount }}
                </span>
              </div>
              <div class="flex justify-between items-center py-2 border-b border-gray-100">
                <span class="text-sm text-gray-600">Valor Médio Locação</span>
                <span class="text-sm font-semibold text-gray-900">
                  {{ formatCurrency(averageMargin) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Gráfico de Barras por Mês -->
        <div class="mt-8 bg-white rounded-lg shadow-sm p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Receitas x Despesas por mês</h3>
          <div v-if="monthlySeries.length === 0" class="text-sm text-gray-500">
            Não há dados suficientes para exibir o gráfico no período selecionado.
          </div>
          <div v-else class="w-full overflow-x-auto">
            <div class="min-w-[500px]">
              <div class="flex items-end justify-start gap-4 h-56 border-b border-gray-200 pb-4">
                <div
                  v-for="item in monthlySeries"
                  :key="item.key"
                  class="flex flex-col items-center text-xs text-gray-600 w-16"
                >
                  <div class="flex items-end gap-1 w-full justify-center">
                    <div
                      class="w-4 bg-green-500 rounded-t"
                      :style="{ height: barHeight(item.revenue) }"
                      :title="`Receita ${item.label}: ${formatCurrency(item.revenue)}`"
                    ></div>
                    <div
                      class="w-4 bg-red-500 rounded-t"
                      :style="{ height: barHeight(item.expense) }"
                      :title="`Despesa ${item.label}: ${formatCurrency(item.expense)}`"
                    ></div>
                  </div>
                  <div class="mt-2 text-[11px] text-gray-700 whitespace-nowrap">
                    {{ item.label }}
                  </div>
                </div>
              </div>
              <div class="mt-3 flex items-center gap-4 text-xs text-gray-600">
                <div class="flex items-center gap-1">
                  <span class="inline-block w-3 h-3 rounded-sm bg-green-500"></span>
                  <span>Receitas</span>
                </div>
                <div class="flex items-center gap-1">
                  <span class="inline-block w-3 h-3 rounded-sm bg-red-500"></span>
                  <span>Despesas</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Gráfico de Linhas por Mês -->
        <div class="mt-6 bg-white rounded-lg shadow-sm p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Evolução mensal de Receitas e Despesas</h3>
          <div v-if="monthlySeries.length === 0" class="text-sm text-gray-500">
            Não há dados suficientes para exibir o gráfico no período selecionado.
          </div>
          <div v-else class="w-full overflow-x-auto">
            <div class="min-w-[500px]">
              <svg
                :width="(monthlySeries.length - 1) * 80 + 40"
                :height="150"
                class="text-xs text-gray-500"
              >
                <line
                  x1="0"
                  :y1="lineChartHeight"
                  :x2="(monthlySeries.length - 1) * 80 + 40"
                  :y2="lineChartHeight"
                  stroke="#e5e7eb"
                />
                <polyline
                  :points="revenueLinePoints"
                  fill="none"
                  stroke="#16a34a"
                  stroke-width="2"
                />
                <polyline
                  :points="expenseLinePoints"
                  fill="none"
                  stroke="#ef4444"
                  stroke-width="2"
                />
                <g v-for="(item, index) in monthlySeries" :key="item.key">
                  <text
                    :x="index * 80"
                    :y="lineChartHeight + 14"
                    class="fill-gray-600 text-[10px]"
                  >
                    {{ item.label }}
                  </text>
                </g>
              </svg>
              <div class="mt-3 flex items-center gap-4 text-xs text-gray-600">
                <div class="flex items-center gap-1">
                  <span class="inline-block w-3 h-3 rounded-sm bg-green-500"></span>
                  <span>Receitas</span>
                </div>
                <div class="flex items-center gap-1">
                  <span class="inline-block w-3 h-3 rounded-sm bg-red-500"></span>
                  <span>Despesas</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Tabelas agrupadas (Comissões / Receitas / Serviços / Despesas) -->
        <div class="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">


          <div class="bg-white rounded-lg shadow-sm p-6">
            <h3 class="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span class="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-100 text-green-700">
                <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 5v14" />
                  <path d="M5 12h14" />
                </svg>
              </span>
              Receitas por Imóvel
            </h3>

            <div v-if="receitaRows.length === 0" class="text-sm text-gray-500">
              Não há receitas para exibir no período selecionado.
            </div>

            <div v-else class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Imóvel</th>
                    <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Valor Total</th>
                    <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Valor Recebido</th>
                    <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Saldo a Receber</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="row in receitaRows" :key="String(row.property_id || row.property_name)" class="hover:bg-gray-50">
                    <td
                      class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-[14rem]"
                      :title="row.property_name || undefined"
                    >
                      {{ truncate(row.property_name, 20) || '-' }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-gray-900">
                      {{ formatCurrency(row.total) }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-gray-700">
                      {{ formatCurrency(row.recebido) }}
                    </td>
                    <td
                      class="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold"
                      :class="row.saldo >= 0 ? 'text-emerald-600' : 'text-red-600'"
                    >
                      {{ formatCurrency(row.saldo) }}
                    </td>
                  </tr>
                </tbody>
                <tfoot class="bg-gray-50">
                  <tr>
                    <td class="px-6 py-3 whitespace-nowrap text-sm font-semibold text-gray-800">
                      Total
                    </td>
                    <td class="px-6 py-3 whitespace-nowrap text-sm font-semibold text-right text-gray-900">
                      {{ formatCurrency(receitaTotals.total) }}
                    </td>
                    <td class="px-6 py-3 whitespace-nowrap text-sm font-semibold text-right text-gray-900">
                      {{ formatCurrency(receitaTotals.recebido) }}
                    </td>
                    <td
                      class="px-6 py-3 whitespace-nowrap text-sm font-semibold text-right"
                      :class="receitaTotals.saldo >= 0 ? 'text-emerald-600' : 'text-red-600'"
                    >
                      {{ formatCurrency(receitaTotals.saldo) }}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <div class="bg-white rounded-lg shadow-sm p-6">
            <h3 class="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span class="inline-flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 text-indigo-700">
                <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="8" r="3" />
                  <path d="M5 21a7 7 0 0114 0" />
                </svg>
              </span>
              Comissões por Corretor
            </h3>

            <div v-if="commissionRows.length === 0" class="text-sm text-gray-500">
              Não há comissões para exibir no período selecionado.
            </div>

            <div v-else class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Corretor</th>
                    <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Valor Total</th>
                    <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Valor Pago</th>
                    <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Saldo a Pagar</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="row in commissionRows" :key="String(row.seller_id || 'none')" class="hover:bg-gray-50">
                    <td
                      class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-[14rem]"
                      :title="row.seller_name || undefined"
                    >
                      {{ row.seller_name ? truncate(row.seller_name, 20) : 'Sem corretor' }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-gray-900">
                      {{ formatCurrency(row.total) }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-gray-700">
                      {{ formatCurrency(row.paid) }}
                    </td>
                    <td
                      class="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold"
                      :class="row.saldo >= 0 ? 'text-emerald-600' : 'text-red-600'"
                    >
                      {{ formatCurrency(row.saldo) }}
                    </td>
                  </tr>
                </tbody>
                <tfoot class="bg-gray-50">
                  <tr>
                    <td class="px-6 py-3 whitespace-nowrap text-sm font-semibold text-gray-800">
                      Total
                    </td>
                    <td class="px-6 py-3 whitespace-nowrap text-sm font-semibold text-right text-gray-900">
                      {{ formatCurrency(commissionTotals.total) }}
                    </td>
                    <td class="px-6 py-3 whitespace-nowrap text-sm font-semibold text-right text-gray-900">
                      {{ formatCurrency(commissionTotals.paid) }}
                    </td>
                    <td
                      class="px-6 py-3 whitespace-nowrap text-sm font-semibold text-right"
                      :class="commissionTotals.saldo >= 0 ? 'text-emerald-600' : 'text-red-600'"
                    >
                      {{ formatCurrency(commissionTotals.saldo) }}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>          

          <div class="bg-white rounded-lg shadow-sm p-6">
            <h3 class="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span class="inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-100 text-amber-700">
                <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </span>
              Serviços Agendados
            </h3>

            <div v-if="servicesRows.length === 0" class="text-sm text-gray-500">
              Não há serviços para exibir no período selecionado.
            </div>

            <div v-else class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo de serviço</th>
                    <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Valor Pago</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="row in servicesRows" :key="'svc-' + String(row.service_type_id ?? 'nil') + '-' + row.name" class="hover:bg-gray-50">
                    <td
                      class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-[20rem]"
                      :title="row.name || undefined"
                    >
                      {{ truncate(row.name, 40) || '-' }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-gray-700">
                      {{ formatCurrency(row.paid) }}
                    </td>
                  </tr>
                </tbody>
                <tfoot class="bg-gray-50">
                  <tr>
                    <td class="px-6 py-3 whitespace-nowrap text-sm font-semibold text-gray-800">
                      Total
                    </td>
                    <td class="px-6 py-3 whitespace-nowrap text-sm font-semibold text-right text-gray-900">
                      {{ formatCurrency(servicesTotals.paid) }}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <div class="bg-white rounded-lg shadow-sm p-6">
            <h3 class="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span class="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-100 text-red-700">
                <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M18 6L6 18" />
                  <path d="M6 6l12 12" />
                </svg>
              </span>
              Despesas
            </h3>

            <div v-if="expensesRows.length === 0" class="text-sm text-gray-500">
              Não há despesas para exibir no período selecionado.
            </div>

            <div v-else class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo da despesa</th>
                    <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Valor Pago</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="row in expensesRows" :key="'exp-' + String(row.service_type_id ?? 'nil') + '-' + row.name" class="hover:bg-gray-50">
                    <td
                      class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-[20rem]"
                      :title="row.name || undefined"
                    >
                      {{ truncate(row.name, 40) || '-' }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-gray-700">
                      {{ formatCurrency(row.paid) }}
                    </td>
                  </tr>
                </tbody>
                <tfoot class="bg-gray-50">
                  <tr>
                    <td class="px-6 py-3 whitespace-nowrap text-sm font-semibold text-gray-800">
                      Total
                    </td>
                    <td class="px-6 py-3 whitespace-nowrap text-sm font-semibold text-right text-gray-900">
                      {{ formatCurrency(expensesTotals.paid) }}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useApi } from '../composables/useApi.js'
import PropertySelectAsync from './PropertySelectAsync.vue'
import PersonSelectAsync from './PersonSelectAsync.vue'
import SelectWithFilter from './SelectWithFilter.vue'

export default {
  components: {
    PropertySelectAsync,
    PersonSelectAsync,
    SelectWithFilter
  },
  data() {
    const today = new Date()
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
    const startDate = firstDayOfMonth.toISOString().split('T')[0]
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0)
    const endDate = lastDayOfMonth.toISOString().split('T')[0]

    return {
      bookings: [],
      summary: null,
      serverPagy: null,
      loading: false,
      filterType: 'all',
      dateError: '',
      filters: {
        propertyId: '',
        ownerId: '',
        responsibleId: '',
        corretorId: '',
        startDate: startDate,
        endDate: endDate
      },
      users: []
    }
  },
  computed: {
    responsibleUserOptions() {
      return [
        { value: '', label: 'Todos os responsáveis' },
        ...(this.users || []).map(u => {
          const fullName = [u.first_name, u.last_name].filter(Boolean).join(' ').trim()
          return {
            value: u.id,
            label: fullName || u.email || u.phone || String(u.id)
          }
        })
      ]
    },
    movementTypeApiValue() {
      if (this.filterType === 'booking') return 'stay'
      if (this.filterType === 'cleaning') return 'service'
      if (this.filterType === 'expense') return 'expense'
      return null
    },
    totals() {
      const t = this.summary && this.summary.totals
      if (!t) {
        return {
          totalDue: 0,
          totalReceived: 0,
          balanceDue: 0,
          totalPayable: 0,
          totalPaid: 0,
          balancePayable: 0,
          totalServicesPayable: 0,
          totalExpensesPayable: 0
        }
      }
      return {
        totalDue: t.total_due || 0,
        totalReceived: t.total_received || 0,
        balanceDue: t.balance_due || 0,
        totalPayable: t.total_payable || 0,
        totalPaid: t.total_paid || 0,
        balancePayable: t.balance_payable || 0,
        totalServicesPayable: t.total_services_payable || 0,
        totalExpensesPayable: t.total_expenses_payable || 0
      }
    },
    balance() {
      if (this.summary && typeof this.summary.balance === 'number') {
        return this.summary.balance
      }
      return this.totals.totalDue - this.totals.totalPayable
    },
    bookingCount() {
      const s = this.summary && this.summary.stats
      return (s && s.booking_count) || 0
    },
    servicesCount() {
      const s = this.summary && this.summary.stats
      return (s && s.services_count) || 0
    },
    statsExpensesCount() {
      const s = this.summary && this.summary.stats
      return (s && s.expenses_count) || 0
    },
    averageMargin() {
      const s = this.summary && this.summary.stats
      return (s && s.average_stay_due) || 0
    },
    monthlySeries() {
      return (this.summary && this.summary.monthly_series) || []
    },
    maxMonthlyValue() {
      if (!this.monthlySeries.length) return 0
      return this.monthlySeries.reduce((max, item) => {
        return Math.max(max, item.revenue, item.expense)
      }, 0)
    },
    lineChartHeight() {
      return 110
    },
    revenueLinePoints() {
      if (!this.monthlySeries.length || !this.maxMonthlyValue) return ''
      return this.monthlySeries
        .map((item, index) => {
          const x = index * 80
          const y = this.lineY(item.revenue)
          return `${x},${y}`
        })
        .join(' ')
    },
    expenseLinePoints() {
      if (!this.monthlySeries.length || !this.maxMonthlyValue) return ''
      return this.monthlySeries
        .map((item, index) => {
          const x = index * 80
          const y = this.lineY(item.expense)
          return `${x},${y}`
        })
        .join(' ')
    },
    commissionRows() {
      return (this.summary && this.summary.commission_by_seller) || []
    },
    receitaRows() {
      return (this.summary && this.summary.receita_by_property) || []
    },
    servicesRows() {
      return (this.summary && this.summary.services_by_service_type) || []
    },
    expensesRows() {
      return (this.summary && this.summary.expenses_by_service_type) || []
    },
    commissionTotals() {
      const total = this.commissionRows.reduce((sum, r) => sum + (parseFloat(r.total) || 0), 0)
      const paid = this.commissionRows.reduce((sum, r) => sum + (parseFloat(r.paid) || 0), 0)
      const saldo = this.commissionRows.reduce((sum, r) => sum + (parseFloat(r.saldo) || 0), 0)
      return { total, paid, saldo }
    },
    receitaTotals() {
      const total = this.receitaRows.reduce((sum, r) => sum + (parseFloat(r.total) || 0), 0)
      const recebido = this.receitaRows.reduce((sum, r) => sum + (parseFloat(r.recebido) || 0), 0)
      const saldo = this.receitaRows.reduce((sum, r) => sum + (parseFloat(r.saldo) || 0), 0)
      return { total, recebido, saldo }
    },
    servicesTotals() {
      const paid = this.servicesRows.reduce((sum, r) => sum + (parseFloat(r.paid) || 0), 0)
      return { paid }
    },
    expensesTotals() {
      const paid = this.expensesRows.reduce((sum, r) => sum + (parseFloat(r.paid) || 0), 0)
      return { paid }
    }
  },
  mounted() {
    this.loadUsers().finally(() => {
      this.loadBookings()
    })
  },
  methods: {
    async loadUsers() {
      try {
        const { get } = useApi()
        const { data, error } = await get('/api/v1/users')
        if (!error && data) {
          this.users = Array.isArray(data.data) ? data.data : []
        }
      } catch (err) {
        console.error('Error loading users:', err)
        this.users = []
      }
    },
    validateDateRange() {
      const start = this.filters.startDate
      const end = this.filters.endDate

      if (!start || !end) {
        this.dateError = 'Informe data inicial e data final para pesquisar.'
        return false
      }

      const startDate = new Date(start)
      const endDate = new Date(end)

      if (isNaN(startDate) || isNaN(endDate)) return false
      if (endDate < startDate) {
        this.dateError = 'A data final não pode ser menor que a data inicial.'
        return false
      }

      const diffMs = endDate.getTime() - startDate.getTime()
      const diffDays = diffMs / (1000 * 60 * 60 * 24)

      const hasProperty = !!this.filters.propertyId
      const maxDays = hasProperty ? 730 : 365

      if (diffDays > maxDays) {
        if (hasProperty) {
          this.dateError = 'O período máximo para um imóvel específico é de 2 anos.'
        } else {
          this.dateError = 'O período máximo sem selecionar um imóvel é de 1 ano.'
        }
        return false
      }

      this.dateError = ''
      return true
    },
    setFilterType(type) {
      this.filterType = type
      this.loadBookings(1)
    },

    async loadBookings(page = 1) {
      if (!this.filters.startDate) {
        return
      }

      if (!this.validateDateRange()) {
        return
      }

      if (page === 1) {
        this.loading = true
      }

      try {
        const { get } = useApi()
        const params = new URLSearchParams()

        if (this.filters.propertyId) {
          params.append('property_id', this.filters.propertyId)
        }
        if (this.filters.ownerId) {
          params.append('owner_id', this.filters.ownerId)
        }
        if (this.filters.responsibleId) {
          params.append('responsible_id', this.filters.responsibleId)
        }
        if (this.filters.corretorId) {
          params.append('seller_id', this.filters.corretorId)
        }
        if (this.filters.startDate) {
          params.append('start_date', this.filters.startDate)
        }
        if (this.filters.endDate) {
          params.append('end_date', this.filters.endDate)
        }

        params.append('page', String(page))
        if (this.movementTypeApiValue) {
          params.append('movement_type', this.movementTypeApiValue)
        }

        const queryString = params.toString()
        const url = `/api/v1/stays${queryString ? '?' + queryString : ''}`

        const { data, error } = await get(url)
        if (!error && data) {
          this.bookings = Array.isArray(data.data) ? data.data : []
          this.serverPagy = data.pagy || null
          this.summary = data.summary || null
        }
      } catch (err) {
        console.error('Error loading bookings:', err)
      } finally {
        if (page === 1) {
          this.loading = false
        }
      }
    },
    truncate(text, length = 10) {
      if (!text) return ''
      return text.length > length ? text.slice(0, length) + '…' : text
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(value || 0)
    },
    formatDate(dateString) {
      if (!dateString) return '-'
      const [year, month, day] = dateString.split('-')
      const date = new Date(year, month - 1, day)
      return date.toLocaleDateString('pt-BR')
    },
    calculateMargin(booking) {
      const revenue = parseFloat(booking.total_due) || 0
      const cost = parseFloat(booking.total_payable) || 0
      return revenue - cost
    },
    getMarginColor(booking) {
      const margin = this.calculateMargin(booking)
      if (margin > 0) return 'text-green-600'
      if (margin < 0) return 'text-red-600'
      return 'text-gray-600'
    },
    getPercentage(value) {
      const total = this.totals.totalDue +
        this.totals.totalServicesPayable +
        this.totals.totalExpensesPayable
      if (total === 0) return 0
      return (value / total * 100).toFixed(1)
    },
    nextPage() {
      if (this.serverPagy && this.serverPagy.next) {
        this.loadBookings(this.serverPagy.next)
      }
    },
    prevPage() {
      if (this.serverPagy && this.serverPagy.prev) {
        this.loadBookings(this.serverPagy.prev)
      }
    },
    barHeight(value) {
      if (!this.maxMonthlyValue || value <= 0) return '0px'
      const maxHeight = 180
      const ratio = value / this.maxMonthlyValue
      const height = Math.max(8, ratio * maxHeight)
      return `${height}px`
    },
    lineY(value) {
      if (!this.maxMonthlyValue || value <= 0) {
        return this.lineChartHeight
      }
      const ratio = value / this.maxMonthlyValue
      const minY = 20
      const maxY = this.lineChartHeight
      const y = maxY - (maxY - minY) * ratio
      return y
    }
  }
}
</script>

